/**
 * Spec 039: the company as one view — what «Отделы» says of each staff member: where it works,
 * since when it is idle, how long it worked, what it spent. No React here.
 */

import type { CompanyViewOut, StaffStatus, ViewStaff } from "@/api/units";

export const STAFF_STATUS: Record<StaffStatus, { label: string; tone: "done" | "review" | "blocked" | "needs" | "waiting" }> = {
  here: { label: "работает", tone: "done" },
  running: { label: "работает", tone: "done" },
  other_pc: { label: "работает на другом ПК", tone: "review" },
  pc_lost: { label: "нет связи с ПК", tone: "blocked" },
  waits_launch: { label: "ждёт запуска", tone: "needs" },
  off: { label: "не запущен", tone: "waiting" },
};

export const WORKING: ReadonlySet<StaffStatus> = new Set<StaffStatus>(["here", "other_pc", "running"]);

/** «меньше минуты», «17 мин», «3 ч 20 мин», «2 дн». */
export function span(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return "меньше минуты";
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} ч ${minutes % 60} мин` : `${hours} ч`;
  return `${Math.floor(hours / 24)} дн`;
}

/** A staff member that does not work: «не активен 3 ч 20 мин»; never launched — said so. */
export function idleText(s: Pick<ViewStaff, "inactive_since">, now: number): string {
  if (!s.inactive_since) return "ещё не запускался";
  return `не активен ${span(now - new Date(s.inactive_since).getTime())}`;
}

/** Minutes its console was open: no data is «нет данных», never zero. */
export function workedText(minutes: number | null): string {
  if (minutes === null) return "нет данных";
  return minutes < 1 ? "0 мин" : span(minutes * 60_000);
}

/** «138 тыс.», «1,8 млн». */
export function shortTokens(n: number): string {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".", ",").replace(",0", "")} млн`;
  if (n >= 1_000) return `${Math.round(n / 1_000)} тыс.`;
  return String(n);
}

/** «ПК mywindows (Костя)», or a dash when it works nowhere. */
export function whereText(s: Pick<ViewStaff, "pc">): string {
  return s.pc ? `ПК ${s.pc.name} (${s.pc.owner_name})` : "не на ПК";
}

/** A unit's staff, its head first, as the view gives them. */
export function staffOfUnit(view: CompanyViewOut, unitId: string): ViewStaff[] {
  const unit = view.units.find((u) => u.id === unitId);
  if (!unit) return [];
  const byId = new Map(view.staff.map((s) => [s.agent_id, s]));
  return unit.staff_ids.map((id) => byId.get(id)).filter((s): s is ViewStaff => Boolean(s));
}

/** How many staff members work now, anywhere. */
export function workingCount(view: CompanyViewOut): number {
  return view.staff.filter((s) => WORKING.has(s.status)).length;
}

/** The month's spending of the whole company: its top units, nested ones included. */
export function monthSpent(view: CompanyViewOut): number {
  return view.units.filter((u) => u.parent_id === null).reduce((sum, u) => sum + u.spent_month, 0);
}
